import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import FormLabel from '@mui/material/FormLabel';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import { getRecentCorrelationIds } from '../../lib/correlation';
import { getBrowserInfo } from '../../lib/browserInfo';
import { getRecentRequests } from '../../lib/networkBuffer';
import { getRecentErrors } from '../../lib/errorBuffer';
import { ScreenshotEditor } from './ScreenshotEditor';
import { DiagnosticsDisclosure } from './DiagnosticsDisclosure';

interface BugReportDialogProps {
  open: boolean;
  onClose: () => void;
  screenshotDataUrl: string | null;
  /**
   * SMSPRO-111 — Fehlermeldung, falls der Bildschirmabzug nicht erstellt werden
   * konnte. Bisher lief die Meldung in diesem Fall stillschweigend ohne Bild
   * weiter; der Ausfall war nur in der zugeklappten Diagnose-Box sichtbar.
   */
  screenshotError?: string | null;
  onSuccess: () => void;
}

/**
 * SMSPRO-187 — Einordnung der Meldung. Ohne dieses Feld legte das Werkzeug JEDE
 * Meldung als Jira-Typ "Bug" an: in der Testrunde vom 04.08. waren 11 von 25
 * "Bugs" in Wahrheit Ausbauwünsche und mussten von Hand umtypisiert werden.
 * Damit ist jede Auswertung nach Vorgangstyp verzerrt.
 *
 * Die Zuordnung auf den Jira-Typ passiert im BFF (BugReportService), nicht hier —
 * das Frontend schickt nur die fachliche Art mit.
 */
export type BugReportKind = 'bug' | 'improvement' | 'feature';

interface SubmitResult {
  ticketNumber?: string;
  jiraKey?: string;
  jiraUrl?: string;
}

interface BugReportData {
  id: string;
  ticket_number: string;
  jira_key?: string;
  jira_url?: string;
}

export function BugReportDialog({ open, onClose, screenshotDataUrl, screenshotError, onSuccess }: BugReportDialogProps) {
  const { t } = useTranslation();
  // Vorbelegung "Fehler": der schnelle Weg bleibt exakt der bisherige.
  const [kind, setKind] = useState<BugReportKind>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [reproSteps, setReproSteps] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SubmitResult | null>(null);
  // Bearbeiteter Screenshot (Pfeile/Schwärzen); initial = das aufgenommene Bild.
  const [editedScreenshot, setEditedScreenshot] = useState<string | null>(screenshotDataUrl);
  useEffect(() => { setEditedScreenshot(screenshotDataUrl); }, [screenshotDataUrl]);

  const resetAndClose = () => {
    setKind('bug');
    setTitle('');
    setDescription('');
    setReproSteps('');
    setError(null);
    setResult(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        // PHQ nutzt das SMS-PRO-BFF mit; das app-Feld steuert das Jira-Label und
        // trennt PHQ-Meldungen von SMS-PRO. Ohne dieses Feld landet die Meldung als smspro.
        app: 'projecthq',
        // Der komplette Diagnose-Kontext unten wird bei ALLEN drei Arten mitgeschickt —
        // auch ein Ausbauwunsch profitiert von Route, Build-Nummer und Correlation-IDs.
        kind,
        title: title.trim(),
        description: description.trim(),
        reproSteps: reproSteps.trim() || undefined,
        currentUrl: window.location.href,
        route: window.location.pathname,
        userAgent: navigator.userAgent,
        browser: getBrowserInfo(),
        viewport: { width: window.innerWidth, height: window.innerHeight },
        language: navigator.language,
        timestamp: new Date().toISOString(),
        screenshot: editedScreenshot,
        network: getRecentRequests(25),
        consoleErrors: getRecentErrors(15),
        diagnostics: {
          logBuffer: logger.formatBufferAsText(),
          correlationIds: getRecentCorrelationIds(),
          verbosity: logger.getVerbosity(),
          buildVersion: __APP_VERSION__,
        },
      };

      const resp = await apiClient.post<BugReportData>('/api/v1/bug-reports', payload);
      logger.info('ui', 'bug report submitted', { kind, titleLength: title.length });
      onSuccess();
      setResult({
        ticketNumber: resp?.data?.ticket_number,
        jiraKey: resp?.data?.jira_key,
        jiraUrl: resp?.data?.jira_url,
      });
    } catch (err) {
      logger.error('error', 'bug report failed', { error: err instanceof Error ? err.message : String(err) });
      const msg = err instanceof Error ? err.message : '';
      setError(msg || t('bugReport.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={result ? resetAndClose : onClose} fullScreen data-bug-report-dialog>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {result ? t('bugReport.successTitle') : t('bugReport.dialogTitle')}
        <IconButton onClick={result ? resetAndClose : onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', py: 2 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
            {result.jiraKey ? (
              <>
                <Typography>{t('bugReport.successJira')}</Typography>
                <Button
                  variant="outlined"
                  endIcon={<OpenInNewIcon />}
                  component={Link}
                  href={result.jiraUrl}
                  target="_blank"
                  rel="noopener"
                >
                  {result.jiraKey} — {t('bugReport.openInJira')}
                </Button>
              </>
            ) : (
              <Typography>
                {t('bugReport.successStored')}: <strong>{result.ticketNumber}</strong>
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1400, mx: 'auto', width: '100%' }}>
            {!screenshotDataUrl && screenshotError && (
              <Alert severity="warning" variant="outlined">
                {t(
                  'bugReport.screenshotFailed',
                  'Der Bildschirmabzug konnte nicht erstellt werden. Die Meldung kann ohne Bild gesendet werden.',
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
                  {screenshotError}
                </Typography>
              </Alert>
            )}
            {screenshotDataUrl && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('bugReport.screenshotPreview')}
                </Typography>
                <ScreenshotEditor src={screenshotDataUrl} onChange={setEditedScreenshot} />
              </Box>
            )}
            <Box>
              <FormLabel required sx={{ display: 'block', mb: 0.5, fontSize: '0.875rem' }}>
                {t('bugReport.kindLabel', 'Art der Meldung')}
              </FormLabel>
              <ToggleButtonGroup
                value={kind}
                exclusive
                size="small"
                color="primary"
                aria-label={t('bugReport.kindLabel', 'Art der Meldung')}
                // Abwählen ist nicht möglich (null wird verworfen) — das Feld ist Pflicht
                // und bleibt damit immer auf einem gültigen Wert.
                onChange={(_e, value: BugReportKind | null) => { if (value) setKind(value); }}
              >
                <ToggleButton value="bug" data-bug-report-kind="bug">
                  {t('bugReport.kindBug', 'Fehler')}
                </ToggleButton>
                <ToggleButton value="improvement" data-bug-report-kind="improvement">
                  {t('bugReport.kindImprovement', 'Verbesserung')}
                </ToggleButton>
                <ToggleButton value="feature" data-bug-report-kind="feature">
                  {t('bugReport.kindFeature', 'Neue Funktion')}
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {t(
                  'bugReport.kindHint',
                  'Bestimmt den Vorgangstyp in Jira. Diagnose-Daten werden in allen Fällen mitgeschickt.',
                )}
              </Typography>
            </Box>
            <TextField
              label={t('bugReport.titleLabel')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
              required
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label={t('bugReport.descriptionLabel')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              required
              multiline
              rows={4}
              inputProps={{ maxLength: 5000 }}
            />
            <TextField
              label={t('bugReport.reproLabel')}
              placeholder={t('bugReport.reproPlaceholder')}
              value={reproSteps}
              onChange={(e) => setReproSteps(e.target.value)}
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 3000 }}
            />
            <DiagnosticsDisclosure hasScreenshot={!!screenshotDataUrl} />
            <Typography variant="caption" color="text.secondary">
              {t('bugReport.autoCapture')}: {window.location.href}
            </Typography>
            {error && (
              <Alert severity="error" variant="outlined">
                {error}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button variant="contained" onClick={resetAndClose}>
            {t('bugReport.close')}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={submitting}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting || !title.trim() || !description.trim()}
              startIcon={submitting ? <CircularProgress size={16} /> : undefined}
            >
              {t('bugReport.submit')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
